import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Bell, Globe, MessageSquare, Settings as SettingsIcon, Zap, Key, Eye, EyeOff, CheckCircle2, Trash2, ExternalLink, Cpu } from 'lucide-react';
import {
  getStoredGroqKey, saveGroqKey, clearGroqKey,
  getStoredGeminiKey, saveGeminiKey, clearGeminiKey,
  getStoredOpenRouterKey, saveOpenRouterKey, clearOpenRouterKey,
  getActiveProvider,
} from '../services/openai';
import { PLATFORM_DEFS, getSocialHandles, saveSocialHandle, clearSocialHandle } from '../services/socialHandles';

type TabType = 'pipeline' | 'sources' | 'notifications' | 'distribution';

interface Source {
  id: string;
  name: string;
  enabled: boolean;
  category: string;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('pipeline');

  // Groq key state
  const [groqKeyInput, setGroqKeyInput] = useState('');
  const [savedGroqKey, setSavedGroqKey] = useState('');
  const [showGroqKey, setShowGroqKey] = useState(false);
  const [groqKeySaved, setGroqKeySaved] = useState(false);

  // Gemini key state
  const [geminiKeyInput, setGeminiKeyInput] = useState('');
  const [savedGeminiKey, setSavedGeminiKey] = useState('');
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [geminiKeySaved, setGeminiKeySaved] = useState(false);

  // OpenRouter key state
  const [orKeyInput, setOrKeyInput] = useState('');
  const [savedOrKey, setSavedOrKey] = useState('');
  const [showOrKey, setShowOrKey] = useState(false);
  const [orKeySaved, setOrKeySaved] = useState(false);

  const [activeProvider, setActiveProvider] = useState(getActiveProvider());

  const refreshProvider = () => setActiveProvider(getActiveProvider());

  useEffect(() => {
    setSavedGroqKey(getStoredGroqKey());
    setSavedGeminiKey(getStoredGeminiKey());
    setSavedOrKey(getStoredOpenRouterKey());
    refreshProvider();
  }, []);

  const handleSaveGroqKey = () => {
    if (!groqKeyInput.trim()) return;
    saveGroqKey(groqKeyInput.trim());
    setSavedGroqKey(groqKeyInput.trim());
    setGroqKeyInput('');
    setGroqKeySaved(true);
    refreshProvider();
    setTimeout(() => setGroqKeySaved(false), 3000);
  };
  const handleClearGroqKey = () => { clearGroqKey(); setSavedGroqKey(''); setGroqKeyInput(''); refreshProvider(); };

  const handleSaveGeminiKey = () => {
    if (!geminiKeyInput.trim()) return;
    saveGeminiKey(geminiKeyInput.trim());
    setSavedGeminiKey(geminiKeyInput.trim());
    setGeminiKeyInput('');
    setGeminiKeySaved(true);
    refreshProvider();
    setTimeout(() => setGeminiKeySaved(false), 3000);
  };
  const handleClearGeminiKey = () => { clearGeminiKey(); setSavedGeminiKey(''); setGeminiKeyInput(''); refreshProvider(); };

  const handleSaveOrKey = () => {
    if (!orKeyInput.trim()) return;
    saveOpenRouterKey(orKeyInput.trim());
    setSavedOrKey(orKeyInput.trim());
    setOrKeyInput('');
    setOrKeySaved(true);
    refreshProvider();
    setTimeout(() => setOrKeySaved(false), 3000);
  };
  const handleClearOrKey = () => { clearOpenRouterKey(); setSavedOrKey(''); setOrKeyInput(''); refreshProvider(); };

  // Pipeline Config State
  const [autoRun, setAutoRun] = useState(true);
  const [weekStartDay, setWeekStartDay] = useState('monday');
  const [contentLanguage, setContentLanguage] = useState('english');
  const [tone, setTone] = useState('professional');

  // Sources State
  const [sources, setSources] = useState<Source[]>([
    { id: '1', name: 'Behance', enabled: true, category: 'Design' },
    { id: '2', name: 'Dribbble', enabled: true, category: 'Design' },
    { id: '3', name: 'Awwwards', enabled: true, category: 'Design' },
    { id: '4', name: 'Pinterest', enabled: true, category: 'Design' },
    { id: '5', name: 'Instagram Design', enabled: true, category: 'Design' },
    { id: '6', name: 'Designboom', enabled: true, category: 'Design' },
    { id: '7', name: 'Dezeen', enabled: true, category: 'Architecture' },
    { id: '8', name: 'ArchDaily', enabled: true, category: 'Architecture' },
    { id: '9', name: 'Core77', enabled: false, category: 'Industrial Design' },
    { id: '10', name: 'Yanko Design', enabled: true, category: 'Product Design' },
    { id: '11', name: 'Design Milk', enabled: true, category: 'Design' },
    { id: '12', name: "It's Nice That", enabled: true, category: 'Creative' },
    { id: '13', name: 'Creative Boom', enabled: true, category: 'Creative' },
    { id: '14', name: 'The Dieline', enabled: false, category: 'Packaging' },
    { id: '15', name: 'Brand New', enabled: true, category: 'Branding' },
    { id: '16', name: 'Abduzeedo', enabled: true, category: 'Design' },
    { id: '17', name: 'Colossal', enabled: true, category: 'Art' },
    { id: '18', name: 'Designspiration', enabled: true, category: 'Design' },
    { id: '19', name: 'Mindsparkle Mag', enabled: false, category: 'Design' },
    { id: '20', name: 'Design You Trust', enabled: true, category: 'Design' },
    { id: '21', name: 'Fubiz', enabled: true, category: 'Creative' },
    { id: '22', name: 'Trend Hunter', enabled: true, category: 'Trends' },
    { id: '23', name: 'Cool Hunting', enabled: true, category: 'Trends' },
    { id: '24', name: 'PSFK', enabled: false, category: 'Innovation' },
    { id: '25', name: 'FastCo Design', enabled: true, category: 'Design' },
    { id: '26', name: 'Design Week', enabled: true, category: 'Design' },
    { id: '27', name: 'Communication Arts', enabled: true, category: 'Design' },
    { id: '28', name: 'Print Magazine', enabled: false, category: 'Design' },
    { id: '29', name: 'Eye on Design', enabled: true, category: 'Design' },
    { id: '30', name: 'AIGA', enabled: true, category: 'Design' },
    { id: '31', name: 'Smashing Magazine', enabled: true, category: 'Web Design' },
    { id: '32', name: 'CSS-Tricks', enabled: true, category: 'Web Design' },
    { id: '33', name: 'Webdesigner Depot', enabled: false, category: 'Web Design' },
    { id: '34', name: 'SiteInspire', enabled: true, category: 'Web Design' },
    { id: '35', name: 'Awwwards Blog', enabled: true, category: 'Web Design' },
    { id: '36', name: 'UX Design', enabled: true, category: 'UX' },
    { id: '37', name: 'Nielsen Norman Group', enabled: true, category: 'UX' },
    { id: '38', name: 'UX Collective', enabled: true, category: 'UX' },
    { id: '39', name: 'Interaction Design', enabled: false, category: 'UX' },
    { id: '40', name: 'UX Planet', enabled: true, category: 'UX' },
    { id: '41', name: 'Material Design', enabled: true, category: 'UI' },
    { id: '42', name: 'UI Movement', enabled: true, category: 'UI' },
    { id: '43', name: 'Collect UI', enabled: true, category: 'UI' },
    { id: '44', name: 'Mobbin', enabled: true, category: 'Mobile UI' },
    { id: '45', name: 'Pttrns', enabled: false, category: 'Mobile UI' },
    { id: '46', name: 'Typewolf', enabled: true, category: 'Typography' },
    { id: '47', name: 'Fonts In Use', enabled: true, category: 'Typography' },
    { id: '48', name: 'I Love Typography', enabled: false, category: 'Typography' },
    { id: '49', name: 'Coolors', enabled: true, category: 'Color' },
    { id: '50', name: 'Color Hunt', enabled: true, category: 'Color' },
    { id: '51', name: 'Unsplash', enabled: true, category: 'Photography' },
    { id: '52', name: 'Pexels', enabled: true, category: 'Photography' },
    { id: '53', name: 'Adobe Stock Trends', enabled: true, category: 'Stock' },
    { id: '54', name: 'Shutterstock Trends', enabled: false, category: 'Stock' },
    { id: '55', name: 'Getty Images Trends', enabled: true, category: 'Stock' },
    { id: '56', name: 'Pantone', enabled: true, category: 'Color Trends' },
    { id: '57', name: 'WGSN', enabled: false, category: 'Trend Forecasting' },
    { id: '58', name: 'TrendWatching', enabled: true, category: 'Trends' },
    { id: '59', name: 'Reddit Design', enabled: true, category: 'Community' },
    { id: '60', name: 'Designer News', enabled: true, category: 'Community' },
    { id: '61', name: 'Product Hunt', enabled: true, category: 'Products' },
    { id: '62', name: 'Muzli', enabled: true, category: 'Design' },
    { id: '63', name: 'Sidebar', enabled: true, category: 'Design' },
    { id: '64', name: 'Design Systems Repo', enabled: false, category: 'Design Systems' },
    { id: '65', name: 'Adweek', enabled: true, category: 'Advertising' },
  ]);

  // Notification State
  const [inAppNotifications, setInAppNotifications] = useState(true);
  const [notificationTiming, setNotificationTiming] = useState('wednesday');
  const [approvalExpiryHours, setApprovalExpiryHours] = useState(48);

  // Social Handles State
  const [handles, setHandles] = useState<Record<string, string>>(getSocialHandles());
  const [handleInputs, setHandleInputs] = useState<Record<string, string>>({});
  const [savedPlatform, setSavedPlatform] = useState('');

  const handleSaveHandle = (platform: string) => {
    const val = handleInputs[platform] || '';
    if (!val.trim()) return;
    saveSocialHandle(platform, val);
    setHandles(getSocialHandles());
    setHandleInputs(prev => ({ ...prev, [platform]: '' }));
    setSavedPlatform(platform);
    setTimeout(() => setSavedPlatform(''), 3000);
  };

  const handleClearHandle = (platform: string) => {
    clearSocialHandle(platform);
    setHandles(getSocialHandles());
  };

  const toggleSource = (id: string) => {
    setSources(sources.map(source =>
      source.id === id ? { ...source, enabled: !source.enabled } : source
    ));
  };

  const tabs = [
    { id: 'pipeline' as TabType, label: 'Pipeline Config', icon: Zap },
    { id: 'sources' as TabType, label: 'Sources & Filters', icon: Globe },
    { id: 'notifications' as TabType, label: 'Notification Preferences', icon: Bell },
    { id: 'distribution' as TabType, label: 'Distribution Accounts', icon: MessageSquare },
  ];

  return (
    <Layout>
      <div className="p-3 sm:p-6 lg:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-5 sm:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <SettingsIcon className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
            <h1 className="text-2xl sm:text-4xl font-bold text-white">Settings</h1>
          </div>
          <p className="text-gray-400 text-sm">Configure your content factory preferences and integrations</p>
        </div>

        {/* ── Active Provider Status Banner ── */}
        {activeProvider.name === 'None' ? (
          <div className="flex items-start gap-3 rounded-xl px-5 py-4 mb-6 border bg-red-500/10 border-red-500/30">
            <Cpu className="w-4 h-4 flex-shrink-0 text-red-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-300">No AI Provider Configured</p>
              <p className="text-xs text-slate-400 mt-0.5">Add at least one API key below to enable content generation. Groq is recommended — it’s free and takes 30 seconds to set up.</p>
            </div>
            <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer"
              className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 whitespace-nowrap">
              Get Groq key →
            </a>
          </div>
        ) : (
          <div className={`flex items-center gap-3 rounded-xl px-5 py-3 mb-6 border ${
            activeProvider.name === 'Groq' ? 'bg-emerald-500/10 border-emerald-500/30' :
            activeProvider.name === 'Gemini' ? 'bg-blue-500/10 border-blue-500/30' :
            'bg-purple-500/10 border-purple-500/30'
          }`}>
            <Cpu className={`w-4 h-4 flex-shrink-0 ${
              activeProvider.name === 'Groq' ? 'text-emerald-400' : activeProvider.name === 'Gemini' ? 'text-blue-400' : 'text-purple-400'
            }`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${
                activeProvider.name === 'Groq' ? 'text-emerald-300' : activeProvider.name === 'Gemini' ? 'text-blue-300' : 'text-purple-300'
              }`}>
                Active AI Provider: {activeProvider.name} — {activeProvider.model}
              </p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${
              activeProvider.name === 'Groq' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
              activeProvider.name === 'Gemini' ? 'bg-blue-500/20 text-blue-300 border-blue-500/40' :
              'bg-purple-500/20 text-purple-300 border-purple-500/40'
            }`}>{activeProvider.badge}</span>
          </div>
        )}

        {/* ── Free AI Provider Comparison ── */}
        <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 mb-6">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-widest mb-3">AI Provider Chain — All Completely Free</p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { rank: '1st', name: 'Groq', model: 'LLaMA 3.3 70B', limit: '14,400 req/day', note: '~500 tok/s', color: 'emerald', url: 'https://console.groq.com' },
              { rank: '2nd', name: 'Gemini', model: 'Gemini 2.0 Flash', limit: '1,500 req/day', note: '1M context', color: 'blue', url: 'https://aistudio.google.com/apikey' },
              { rank: '3rd', name: 'OpenRouter', model: 'Llama 3.3 70B', limit: 'Free models', note: 'No credit card', color: 'orange', url: 'https://openrouter.ai/keys' },
            ].map(p => {
              const colors: Record<string, { border: string; bg: string; text: string; badge: string }> = {
                emerald: { border: 'rgb(16 185 129/0.2)', bg: 'rgb(16 185 129/0.05)', text: '#34d399', badge: 'rgb(16 185 129/0.2)' },
                blue:    { border: 'rgb(59 130 246/0.2)', bg: 'rgb(59 130 246/0.05)', text: '#60a5fa', badge: 'rgb(59 130 246/0.2)' },
                orange:  { border: 'rgb(249 115 22/0.2)', bg: 'rgb(249 115 22/0.05)', text: '#fb923c', badge: 'rgb(249 115 22/0.2)' },
              };
              const c = colors[p.color];
              return (
                <div key={p.name} className="rounded-lg border p-3" style={{ borderColor: c.border, background: c.bg }}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ color: c.text, background: c.badge }}>{p.rank}</span>
                    <span className="text-sm font-semibold text-slate-200">{p.name}</span>
                  </div>
                  <p className="text-[11px] text-slate-400">{p.model}</p>
                  <p className="text-[10px] text-slate-500 mt-1">{p.limit} · {p.note}</p>
                  <a href={p.url} target="_blank" rel="noopener noreferrer"
                    className="mt-2 flex items-center gap-1 text-[10px] hover:text-slate-300" style={{ color: c.text }}>
                    Get free key <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Groq API Key Panel (Primary — Free) ── */}
        <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-6 mb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-lg bg-emerald-600/20 flex items-center justify-center">
              <Key className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-slate-100 font-semibold">Groq API Key</h2>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold border border-emerald-500/30">PRIMARY · FREE</span>
              </div>
              <p className="text-slate-500 text-xs">LLaMA 3.3 70B · 14,400 req/day · ~500 tok/s · Best quality</p>
            </div>
            <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
              Get key <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-xs text-slate-500 mb-3 ml-12">Key starts with <code className="text-emerald-400 bg-slate-800 px-1 rounded">gsk_</code></p>

          {savedGroqKey ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-slate-800 border border-emerald-500/30 rounded-lg px-4 py-2.5 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-emerald-400 text-sm font-mono">
                  {showGroqKey ? savedGroqKey : savedGroqKey.slice(0, 8) + '••••••••••••••••••••' + savedGroqKey.slice(-4)}
                </span>
              </div>
              <button onClick={() => setShowGroqKey(!showGroqKey)} className="p-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 transition-colors">
                {showGroqKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={handleClearGroqKey} className="p-2.5 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <input
                type="password"
                value={groqKeyInput}
                onChange={(e) => setGroqKeyInput(e.target.value)}
                placeholder="gsk_…"
                className="flex-1 bg-slate-800 border border-slate-700 focus:border-emerald-500 rounded-lg px-4 py-2.5 text-slate-100 text-sm font-mono placeholder:text-slate-600 outline-none transition-colors"
                onKeyDown={(e) => e.key === 'Enter' && handleSaveGroqKey()}
              />
              <button
                onClick={handleSaveGroqKey}
                disabled={!groqKeyInput.trim()}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                {groqKeySaved ? '✓ Saved!' : 'Save Key'}
              </button>
            </div>
          )}
        </div>

        {/* ── Gemini API Key Panel (Fallback #1 — Free) ── */}
        <div className="bg-slate-900 border border-blue-500/20 rounded-xl p-6 mb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-lg bg-blue-600/20 flex items-center justify-center">
              <Key className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-slate-100 font-semibold">Google Gemini API Key</h2>
                <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[10px] font-bold border border-blue-500/30">FALLBACK #1 · FREE</span>
              </div>
              <p className="text-slate-500 text-xs">Gemini 2.0 Flash · 1,500 req/day free · 1M context window · Used when Groq is rate-limited</p>
            </div>
            <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors">
              Get key <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-xs text-slate-500 mb-3 ml-12">Key starts with <code className="text-blue-400 bg-slate-800 px-1 rounded">AIza</code></p>

          {savedGeminiKey ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-slate-800 border border-blue-500/30 rounded-lg px-4 py-2.5 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <span className="text-blue-400 text-sm font-mono">
                  {showGeminiKey ? savedGeminiKey : savedGeminiKey.slice(0, 8) + '••••••••••••••••••••' + savedGeminiKey.slice(-4)}
                </span>
              </div>
              <button onClick={() => setShowGeminiKey(!showGeminiKey)} className="p-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 transition-colors">
                {showGeminiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={handleClearGeminiKey} className="p-2.5 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <input
                type="password"
                value={geminiKeyInput}
                onChange={(e) => setGeminiKeyInput(e.target.value)}
                placeholder="AIzaSy…"
                className="flex-1 bg-slate-800 border border-slate-700 focus:border-blue-500 rounded-lg px-4 py-2.5 text-slate-100 text-sm font-mono placeholder:text-slate-600 outline-none transition-colors"
                onKeyDown={(e) => e.key === 'Enter' && handleSaveGeminiKey()}
              />
              <button
                onClick={handleSaveGeminiKey}
                disabled={!geminiKeyInput.trim()}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                {geminiKeySaved ? '✓ Saved!' : 'Save Key'}
              </button>
            </div>
          )}
        </div>

        {/* ── OpenRouter API Key Panel (Fallback #2 — Truly Free) ── */}
        <div className="bg-slate-900 border border-orange-500/20 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-lg bg-orange-600/20 flex items-center justify-center">
              <Key className="w-5 h-5 text-orange-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-slate-100 font-semibold">OpenRouter API Key</h2>
                <span className="px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 text-[10px] font-bold border border-orange-500/30">FALLBACK #2 · TRULY FREE</span>
              </div>
              <p className="text-slate-500 text-xs">Llama 3.3 70B (free tier) · No credit card needed · Routes to best available model</p>
            </div>
            <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 transition-colors">
              Get key <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-xs text-slate-500 mb-3 ml-12">Key starts with <code className="text-orange-400 bg-slate-800 px-1 rounded">sk-or-</code></p>
          {savedOrKey ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-slate-800 border border-orange-500/30 rounded-lg px-4 py-2.5 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0" />
                <span className="text-orange-400 text-sm font-mono">
                  {showOrKey ? savedOrKey : savedOrKey.slice(0, 8) + '••••••••••••••••••••' + savedOrKey.slice(-4)}
                </span>
              </div>
              <button onClick={() => setShowOrKey(!showOrKey)} className="p-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 transition-colors">
                {showOrKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={handleClearOrKey} className="p-2.5 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <input type="password" value={orKeyInput} onChange={(e) => setOrKeyInput(e.target.value)}
                placeholder="sk-or-…"
                className="flex-1 bg-slate-800 border border-slate-700 focus:border-orange-500 rounded-lg px-4 py-2.5 text-slate-100 text-sm font-mono placeholder:text-slate-600 outline-none transition-colors"
                onKeyDown={(e) => e.key === 'Enter' && handleSaveOrKey()} />
              <button onClick={handleSaveOrKey} disabled={!orKeyInput.trim()}
                className="px-5 py-2.5 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition-colors text-sm">
                {orKeySaved ? '✓ Saved!' : 'Save Key'}
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-white border border-purple-500/30'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
          {/* Pipeline Config Tab */}
          {activeTab === 'pipeline' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Pipeline Configuration</h2>

                {/* Auto-run Toggle */}
                <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Auto-run Pipeline</h3>
                      <p className="text-gray-400 text-sm">Automatically run pipeline every Monday at 9:00 AM</p>
                    </div>
                    <button
                      onClick={() => setAutoRun(!autoRun)}
                      className={`relative w-14 h-7 rounded-full transition-colors ${
                        autoRun ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-600'
                      }`}
                    >
                      <div
                        className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                          autoRun ? 'transform translate-x-7' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Week Start Day */}
                <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
                  <label className="block text-lg font-semibold text-white mb-3">Week Start Day</label>
                  <select
                    value={weekStartDay}
                    onChange={(e) => setWeekStartDay(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="monday">Monday</option>
                    <option value="sunday">Sunday</option>
                    <option value="saturday">Saturday</option>
                  </select>
                </div>

                {/* Content Language */}
                <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
                  <label className="block text-lg font-semibold text-white mb-3">Content Language</label>
                  <select
                    value={contentLanguage}
                    onChange={(e) => setContentLanguage(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="english">English</option>
                    <option value="spanish">Spanish</option>
                    <option value="french">French</option>
                    <option value="german">German</option>
                    <option value="italian">Italian</option>
                    <option value="portuguese">Portuguese</option>
                    <option value="japanese">Japanese</option>
                    <option value="chinese">Chinese</option>
                  </select>
                </div>

                {/* Tone Selector */}
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <label className="block text-lg font-semibold text-white mb-3">Content Tone</label>
                  <div className="grid grid-cols-3 gap-4">
                    {['professional', 'conversational', 'academic'].map((toneOption) => (
                      <button
                        key={toneOption}
                        onClick={() => setTone(toneOption)}
                        className={`px-6 py-4 rounded-lg font-medium transition-all capitalize ${
                          tone === toneOption
                            ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-white border-2 border-purple-500'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {toneOption}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sources & Filters Tab */}
          {activeTab === 'sources' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">Content Sources</h2>
                  <p className="text-gray-400">Enable or disable trend sources ({sources.filter(s => s.enabled).length}/{sources.length} active)</p>
                </div>
                <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity">
                  + Add Custom Source
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sources.map((source) => (
                  <div
                    key={source.id}
                    className="bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-white font-medium mb-1">{source.name}</h3>
                        <p className="text-gray-400 text-xs">{source.category}</p>
                      </div>
                      <button
                        onClick={() => toggleSource(source.id)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          source.enabled ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-600'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            source.enabled ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">Notification Preferences</h2>

              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">In-App Notifications</h3>
                    <p className="text-gray-400 text-sm">Receive notifications within the dashboard</p>
                  </div>
                  <button
                    onClick={() => setInAppNotifications(!inAppNotifications)}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      inAppNotifications ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                        inAppNotifications ? 'transform translate-x-7' : ''
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <label className="block text-lg font-semibold text-white mb-3">Content Preview Notification</label>
                <p className="text-gray-400 text-sm mb-4">When to receive weekly content preview notifications</p>
                <select
                  value={notificationTiming}
                  onChange={(e) => setNotificationTiming(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="monday">Monday Morning</option>
                  <option value="tuesday">Tuesday Morning</option>
                  <option value="wednesday">Wednesday Morning</option>
                  <option value="thursday">Thursday Morning</option>
                  <option value="friday">Friday Morning</option>
                </select>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <label className="block text-lg font-semibold text-white mb-3">Approval Expiry Time</label>
                <p className="text-gray-400 text-sm mb-4">Hours before pending approvals expire and require re-review</p>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    value={approvalExpiryHours}
                    onChange={(e) => setApprovalExpiryHours(Number(e.target.value))}
                    min="1"
                    max="168"
                    className="w-32 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-gray-400">hours</span>
                </div>
              </div>
            </div>
          )}

          {/* Distribution Accounts Tab */}
          {activeTab === 'distribution' && (
            <div className="space-y-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-1">Connected Accounts</h2>
                <p className="text-gray-400 text-sm">
                  Your handles are saved in your browser only — private and secure.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PLATFORM_DEFS.map((pd) => {
                  const saved = handles[pd.platform] || '';
                  const inp = handleInputs[pd.platform] || '';
                  const justSaved = savedPlatform === pd.platform;
                  return (
                    <div key={pd.platform} className="bg-white/5 rounded-xl p-5 border border-white/10">
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm"
                          style={{ backgroundColor: pd.bgColor, color: pd.color }}
                        >
                          {pd.icon}
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{pd.platform}</h3>
                          {saved ? (
                            <div className="flex items-center gap-1 mt-0.5">
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                              <span className="text-green-400 text-xs font-medium">{saved}</span>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-xs">Not connected</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={inp}
                          onChange={(e) =>
                            setHandleInputs(prev => ({ ...prev, [pd.platform]: e.target.value }))
                          }
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveHandle(pd.platform)}
                          placeholder={pd.placeholder}
                          className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500"
                        />
                        <button
                          onClick={() => handleSaveHandle(pd.platform)}
                          className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all"
                          style={{ backgroundColor: justSaved ? '#22c55e' : pd.bgColor }}
                        >
                          {justSaved ? '✓' : 'Save'}
                        </button>
                        {saved && (
                          <button
                            onClick={() => handleClearHandle(pd.platform)}
                            className="px-3 py-2 rounded-lg bg-white/10 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* LinkedIn Developer App Credentials */}
              <div className="mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452z"/></svg>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">LinkedIn API Credentials</h3>
                    <p className="text-gray-400 text-xs">Required for real LinkedIn publishing. Stored securely in Supabase secrets.</p>
                  </div>
                </div>
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-5 space-y-3">
                  <div className="p-3 bg-blue-800/20 rounded-lg text-xs text-blue-300 space-y-1">
                    <p className="font-semibold text-blue-200">Setup Instructions:</p>
                    <p>1. Go to <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline text-blue-400">linkedin.com/developers/apps</a> and create an app</p>
                    <p>2. Enable <strong>Share on LinkedIn</strong> and <strong>Sign In with LinkedIn using OpenID Connect</strong> products</p>
                    <p>3. Under Auth tab, copy Client ID and Client Secret below</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1 font-medium">Client ID</label>
                      <input
                        type="text"
                        placeholder="e.g. 77abc12def34gh56"
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        onChange={e => localStorage.setItem('uxcf_li_client_id_hint', e.target.value)}
                        defaultValue={localStorage.getItem('uxcf_li_client_id_hint') || ''}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1 font-medium">Client Secret</label>
                      <input
                        type="password"
                        placeholder="Keep this secret!"
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      />
                      <p className="text-xs text-amber-400 mt-1">⚠ Never paste your secret in the browser. Add it directly in Supabase secrets.</p>
                    </div>
                  </div>
                  <a
                    href="https://supabase.com/dashboard/project/_/settings/functions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Supabase Secrets
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
